import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WarningBanner from '../../../../components/WarningBanner';
import Intestazione from './Intestazione';
import conteggio from './conteggio';
import useStili from './stili';

const useStyles = makeStyles((theme) => ({
  riepilogo: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: theme.spacing(6),
    rowGap: theme.spacing(1),
    margin: 0,
    padding: theme.spacing(2),
  },
  voce: {
    margin: 0,
    color: theme.palette.text.secondary,
  },
  valore: {
    margin: 0,
    overflowWrap: 'anywhere',
  },
  nomeFile: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  avviso: {
    marginTop: theme.spacing(2),
  },
  // The banner holds a single paragraph: each sentence is a line of its own
  // inside it, not a paragraph nested in another.
  frase: {
    display: 'block',
  },
}));

// A GeOp with neither procedure nor schede has nothing to lose: the import
// fills it, and the confirmation says so instead of asking to replace it. Only
// two counts of zero mean that: a count that did not arrive asks to replace.
const TESTI = {
  primoImport: {
    titolo: 'Importare la configurazione?',
    testo:
      'La Gestione Operativa di questo esercizio non ha ancora procedure né schede: verrà compilata con il contenuto del file.',
    conferma: 'Importa la configurazione',
  },
  sostituzione: {
    titolo: 'Sostituire la configurazione esistente?',
    testo:
      "Questo esercizio ha già una Gestione Operativa configurata. L'import non fonde il file con quello che c'è: lo sostituisce.",
    conferma: 'Sostituisci la configurazione',
  },
};

// What the replaced configuration takes with it: no version of it is kept,
// and the records gathered under it lose what they refer to.
// Above the backend's warnings: the import goes ahead, and what they name is
// left for the consultant to complete in the configurator afterwards.
const DA_COMPLETARE = "Da completare dopo l'import:";

const PERDITA =
  'La configurazione attuale non viene conservata e non sarà più recuperabile: le modifiche fatte a mano andranno perse, e le registrazioni già raccolte perderanno il riferimento alle schede e ai controlli da cui sono nate.';

// «Verranno sostituite 12 procedure e 34 schede.», leaving out a count of zero;
// the verb agrees with a single item. `null` unless both counts arrived: half
// of them would read as a GeOp with nothing of the other kind.
const fraseQuantita = (procedure, schede) => {
  if (procedure == null || schede == null) {
    return null;
  }
  const voci = [
    [procedure, 'procedura', 'procedure'],
    [schede, 'scheda', 'schede'],
  ].filter(([numero]) => numero > 0);
  const verbo =
    voci.length === 1 && voci[0][0] === 1
      ? 'Verrà sostituita'
      : 'Verranno sostituite';
  return `${verbo} ${voci.map((voce) => conteggio(...voce)).join(' e ')}.`;
};

/**
 * The file passed the check: what is about to be imported, and whether it
 * fills an empty GeOp or replaces the configuration it holds. Nothing has been
 * written yet.
 */
const ConfermaImport = ({
  file,
  riepilogo,
  inScrittura,
  onConferma,
  onAnnulla,
}) => {
  const stili = useStili();
  const classes = useStyles();
  const {
    workspace,
    procedureDaSostituire,
    schedeDaSostituire,
    avvisi = [],
  } = riepilogo;
  const sostituzione = !(
    procedureDaSostituire === 0 && schedeDaSostituire === 0
  );
  const testi = sostituzione ? TESTI.sostituzione : TESTI.primoImport;

  // A row the file's header did not carry is left out, not shown empty.
  const righe = [
    { voce: 'File', valore: file.name, classe: classes.nomeFile },
    { voce: 'Workspace', valore: workspace },
  ].filter(({ valore }) => valore);
  const quantita = fraseQuantita(procedureDaSostituire, schedeDaSostituire);

  return (
    <>
      <DialogContent className={stili.content}>
        <Intestazione titolo={testi.titolo} testo={testi.testo} />
        <dl className={`${stili.riquadro} ${classes.riepilogo}`}>
          {righe.map(({ voce, valore, classe }) => (
            <React.Fragment key={voce}>
              <Typography
                variant="body2"
                component="dt"
                className={classes.voce}
              >
                {voce}
              </Typography>
              <Typography
                variant="body2"
                component="dd"
                className={
                  classe ? `${classes.valore} ${classe}` : classes.valore
                }
              >
                {valore}
              </Typography>
            </React.Fragment>
          ))}
        </dl>
        {avvisi.length > 0 && (
          <WarningBanner className={classes.avviso}>
            <span className={classes.frase}>{DA_COMPLETARE}</span>
            {avvisi.map((avviso) => (
              <span key={avviso} className={classes.frase}>
                {avviso}
              </span>
            ))}
          </WarningBanner>
        )}
        {sostituzione && (
          <WarningBanner className={classes.avviso}>
            {quantita && <span className={classes.frase}>{quantita}</span>}
            <span className={classes.frase}>{PERDITA}</span>
          </WarningBanner>
        )}
      </DialogContent>
      <DialogActions disableSpacing className={stili.actions}>
        <Button
          variant="outlined"
          className={stili.secondario}
          onClick={onAnnulla}
          disabled={inScrittura}
        >
          Annulla
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={stili.primario}
          onClick={onConferma}
          disabled={inScrittura}
          // The spinner replaces the label while the write runs; the name
          // stays so the button does not go silent at that moment.
          aria-label={testi.conferma}
          aria-busy={inScrittura}
        >
          {inScrittura ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            testi.conferma
          )}
        </Button>
      </DialogActions>
    </>
  );
};

ConfermaImport.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
  riepilogo: PropTypes.shape({
    workspace: PropTypes.string,
    procedureDaSostituire: PropTypes.number,
    schedeDaSostituire: PropTypes.number,
    // The backend's warnings, one sentence each: they do not stop the import.
    avvisi: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  inScrittura: PropTypes.bool,
  onConferma: PropTypes.func.isRequired,
  onAnnulla: PropTypes.func.isRequired,
};

export default ConfermaImport;
